import { createElement, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { InboxGroup } from "../api/connectionHub.types";
import SetupPip, { INITIAL_VIDEO_HEIGHT, pipSizeOf } from "../components/pip/SetupPip/SetupPip";
import { PROVIDER_SETUP_GUIDES } from "../constants/providerSetupGuides.constants";
import { setupLinkOf, setupPipOf, type SetupStep } from "../constants/setupSteps.constants";
import { isDocumentPipSupported, openDocumentPip } from "../utils/documentPip";
import { playPip, preloadPip } from "../utils/pipPlayer";

export interface SetupLauncher {
  urlFor: (step: SetupStep) => string | null;
  open: (step: SetupStep) => void;
  pip: ReactNode;
  blockedLabel: string | null;
  openBlocked: () => void;
}

interface OpenPip {
  window: Window;
  step: SetupStep;
}

interface BlockedTab {
  url: string;
  step: SetupStep;
}

const openTab = (url: string, view: Window = window): Window | null => {
  const tab = view.open(url, "_blank");
  if (tab) tab.opener = null;
  return tab;
};

export const useSetupLauncher = (group: InboxGroup, activeStep: SetupStep | null): SetupLauncher => {
  const [openPip, setOpenPip] = useState<OpenPip | null>(null);
  const [blocked, setBlocked] = useState<BlockedTab | null>(null);
  const openRef = useRef<OpenPip | null>(null);
  openRef.current = openPip;

  const fallbackVideo = activeStep && !isDocumentPipSupported() ? setupPipOf(group, activeStep)?.video ?? null : null;
  useEffect(() => { preloadPip(fallbackVideo); }, [fallbackVideo]);

  const closePip = useCallback((): void => {
    openRef.current?.window.close();
    setOpenPip(null);
  }, []);

  useEffect(() => {
    if (openPip && openPip.step !== activeStep) closePip();
  }, [activeStep, openPip, closePip]);

  useEffect(() => {
    if (blocked && blocked.step !== activeStep) setBlocked(null);
  }, [activeStep, blocked]);

  useEffect(() => () => { openRef.current?.window.close(); }, []);

  const open = (step: SetupStep): void => {
    const url = setupLinkOf(group, step);
    if (!url) return;
    setBlocked(null);
    const content = setupPipOf(group, step);

    if (!content || !isDocumentPipSupported()) {
      playPip(content?.video ?? null);
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    const size = pipSizeOf(content, INITIAL_VIDEO_HEIGHT);
    const pipRequest = openDocumentPip(size.width, size.height);
    const tab = openTab(url);
    void pipRequest.then((pipWindow) => {
      if (!pipWindow) {
        if (!tab) window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      pipWindow.addEventListener("pagehide", () => {
        setOpenPip((current) => (current?.window === pipWindow ? null : current));
      }, { once: true });
      setOpenPip({ window: pipWindow, step });
      if (!tab) setBlocked({ url, step });
    });
  };

  const openBlocked = (view: Window = window): void => {
    if (!blocked) return;
    if (openTab(blocked.url, view)) setBlocked(null);
  };

  const openLabel = `Open ${PROVIDER_SETUP_GUIDES[group.inbox.provider]?.displayName ?? "Inbox"}`;
  const content = openPip ? setupPipOf(group, openPip.step) : null;
  const pip = openPip && content
    ? createPortal(
      createElement(SetupPip, {
        content,
        blockedUrl: blocked?.url ?? null,
        openLabel,
        onOpenBlocked: openBlocked,
        onDone: () => {
          window.focus();
          closePip();
        },
      }),
      openPip.window.document.body
    )
    : null;

  return {
    urlFor: (step) => setupLinkOf(group, step),
    open,
    pip,
    blockedLabel: blocked ? openLabel : null,
    openBlocked: () => openBlocked(window),
  };
};
